import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { uniq } from 'lodash-es';
import { AppState } from '../../../../core/reducers';
import { CategoryService } from '../../../../core/lists';
import { FormService } from '../services';
import {
  CancelFormPage,
  ClearFormError,
  CreateForm,
  CreateFormOnServer,
  LoadFormPage,
  FormActionTypes,
  RequestFormPage,
  ThrowFormError,
  ToggleFormActionLoading,
  ToggleFormPageLoading,
  UpdateForm,
  UpdateFormOnServer,
  UpdateFormStatusOnServer,
} from '../actions';

@Injectable()
export class FormEffects {
  showPageLoading = new ToggleFormPageLoading({ pageLoading: true });
  hidePageLoading = new ToggleFormPageLoading({ pageLoading: false });
  showActionLoading = new ToggleFormActionLoading({ actionLoading: true });
  hideActionLoading = new ToggleFormActionLoading({ actionLoading: false });

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private formService: FormService,
    private categoryService: CategoryService,
  ) {}

  @Effect()
  loadFormPage$ = this.actions$.pipe(
    ofType<RequestFormPage>(FormActionTypes.RequestFormPage),
    map((action: RequestFormPage) => action.payload),
    switchMap(({ query }) => {
      this.store.dispatch(this.showPageLoading);

      return forkJoin([this.formService.getFormPage(query), of(query)]).pipe(
        catchError((err) => of(err)),
      );
    }),
    switchMap((res) => {
      if (res.length) {
        let forms = res[0].data;
        const teamIds = [];

        forms?.forEach((form) => teamIds.push(...(form.teams || [])));

        this.store.dispatch(new ClearFormError());
        this.store.dispatch(this.hidePageLoading);

        if (teamIds.length)
          return this.categoryService.getTeamByIds(uniq(teamIds)).pipe(
            map((res1) => {
              const teams = res1.data;

              forms = forms?.map((form) => ({
                ...form,
                teams: form.teams?.map((id) => teams?.find((team) => team.id === id)),
              }));

              return new LoadFormPage({ forms, totalCount: res[0].count, query: res[1] });
            }),
          );

        return of(new LoadFormPage({ forms, totalCount: res[0].count, query: res[1] }));
      }

      this.store.dispatch(new CancelFormPage());

      return of(new ThrowFormError({ error: res }));
    }),
  );

  @Effect()
  createForm$ = this.actions$.pipe(
    ofType<CreateFormOnServer>(FormActionTypes.CreateFormOnServer),
    map((action: CreateFormOnServer) => action.payload),
    mergeMap(({ form, importedFormId }) => {
      this.store.dispatch(this.showActionLoading);

      return this.formService.createForm(form, importedFormId).pipe(
        catchError((err) => of(err)),
        map((res) => {
          if (res.status === 201) {
            this.store.dispatch(new ClearFormError());
            this.store.dispatch(this.hideActionLoading);

            return new CreateForm({ form: res.body?.data });
          }

          return new ThrowFormError({ error: res });
        }),
      );
    }),
  );

  @Effect()
  updateForm$ = this.actions$.pipe(
    ofType<UpdateFormOnServer>(FormActionTypes.UpdateFormOnServer),
    map((action: UpdateFormOnServer) => action.payload),
    mergeMap(({ form }) => {
      this.store.dispatch(this.showActionLoading);

      return this.formService.updateForm(form).pipe(
        catchError((err) => of(err)),
        map((res) => {
          if (res.status === 200) {
            this.store.dispatch(new ClearFormError());
            this.store.dispatch(this.hideActionLoading);

            return new UpdateForm({ form: res.body?.data });
          }

          return new ThrowFormError({ error: res });
        }),
      );
    }),
  );

  @Effect()
  updateFormStatus$ = this.actions$.pipe(
    ofType<UpdateFormStatusOnServer>(FormActionTypes.UpdateFormStatusOnServer),
    map((action: UpdateFormStatusOnServer) => action.payload),
    mergeMap(({ form }) => {
      this.store.dispatch(this.showActionLoading);

      return this.formService.updateFormStatus(form).pipe(
        catchError((err) => of(err)),
        map((res) => {
          if (res.status === 200) {
            this.store.dispatch(new ClearFormError());
            this.store.dispatch(this.hideActionLoading);

            return new UpdateForm({ form: form as any });
          }

          return new ThrowFormError({ error: res });
        }),
      );
    }),
  );
}
